import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthState, useAuthActions } from '@/shared/stores/auth-store';

/**
 * Пропсы для компонента Layout
 */
interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

/**
 * Элемент навигации
 */
interface NavItem {
  href: string;
  icon: string;
  label: string;
}

/**
 * Основной макет админ-панели
 * Включает боковую панель навигации и основную область контента
 * @param children - Содержимое страницы
 * @param title - Заголовок страницы
 * @returns JSX элемент
 */
export const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const location = useLocation();
  const { user } = useAuthState();
  const { logout } = useAuthActions();

  /**
   * Элементы навигации
   */
  const navItems: NavItem[] = [
    {
      href: '/dashboard',
      icon: 'fas fa-home',
      label: 'Главная'
    },
    {
      href: '/doctors',
      icon: 'fas fa-user-md',
      label: 'Врачи'
    },
    {
      href: '/prices',
      icon: 'fas fa-tags',
      label: 'Управление ценами'
    },
    {
      href: '/pages',
      icon: 'fas fa-file-alt',
      label: 'Страницы'
    }
  ];

  /**
   * Проверка активности навигационного элемента
   * @param href - Путь для проверки
   * @returns true если путь активен
   */
  const isActiveRoute = (href: string): boolean => {
    if (href === '/dashboard') {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  /**
   * Обработчик выхода из системы
   */
  const handleLogout = (): void => {
    logout();
  };

  return (
    <div className="drawer lg:drawer-open">
      <input id="drawer-toggle" type="checkbox" className="drawer-toggle" />

      {/* Основной контент */}
      <div className="drawer-content flex flex-col">
        {/* Верхняя панель */}
        <div className="navbar bg-base-100 shadow-sm lg:hidden">
          <div className="flex-none">
            <label htmlFor="drawer-toggle" className="btn btn-square btn-ghost">
              <i className="fas fa-bars"></i>
            </label>
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Алёнушка</h1>
          </div>
        </div>

        {/* Заголовок страницы */}
        {title && (
          <div className="bg-base-100 border-b border-base-300 px-6 py-4">
            <h1 className="text-2xl font-bold text-base-content">{title}</h1>
          </div>
        )}

        {/* Основное содержимое */}
        <main className="flex-1 p-6">{children}</main>
      </div>

      {/* Боковая панель */}
      <div className="drawer-side z-30">
        <label htmlFor="drawer-toggle" className="drawer-overlay"></label>
        <aside className="min-h-full w-60 bg-base-100 border-r border-base-300">
          {/* Заголовок боковой панели */}
          <div className="p-6 border-b border-base-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                <i className="fas fa-stethoscope text-primary-content"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-base-content">
                  Алёнушка
                </h2>
                <p className="text-sm text-base-content/60">Админ-панель</p>
              </div>
            </div>
          </div>

          {/* Навигационное меню */}
          <ul className="menu p-4 space-y-1">
            {navItems.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className={`flex items-center gap-3 ${
                    isActiveRoute(item.href)
                      ? 'bg-primary text-primary-content'
                      : 'text-base-content hover:bg-base-200'
                  }`}
                >
                  <i className={`${item.icon} w-5 text-center`}></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Нижняя часть боковой панели */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-base-300">
            {/* Информация о пользователе */}
            {user && (
              <div className="mb-4 p-3 bg-base-200 rounded-lg">
                <div className="text-sm font-medium text-base-content">
                  {user.name}
                </div>
                <div className="text-xs text-base-content/60">{user.email}</div>
              </div>
            )}

            {/* Кнопка выхода */}
            <button
              onClick={handleLogout}
              className="btn btn-ghost btn-sm w-full justify-start text-error hover:bg-error/10"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Выйти</span>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};
